/**
 * Fits Home to the screen, without scrolling, wherever the screen is tall
 * enough.
 *
 * Home's blocks are measured against the height the body has. If they do not
 * fit, Home lays out once more with everything tightened (`squeeze` 1 in
 * `homeLayout`); if that fits, it settles on the least squeeze that does —
 * interpolated between the two, then checked. A phone too short even at the
 * tightest keeps Home's natural sizes and scrolls, as it always has: there,
 * squeezing would only make everything smaller without removing the scroll.
 * One that misses by only a few points keeps the tightest (NEAR_FIT).
 *
 * The body stays hidden while it measures (a few frames), so nobody sees it
 * change size — the first time, and again after a change of language or
 * window. A fit already found for this window and language is reused at once.
 * `revealAfterMs` shows the body regardless, in case a measurement never
 * arrives.
 *
 * Everything the search needs lives in refs and is read when it runs, so a
 * layout event always measures against the current language and window. (An
 * earlier version captured the first ones in its handlers, and after a change
 * of language it restarted the search for ever: Home jumped between two sizes
 * every third of a second.)
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { LayoutChangeEvent } from 'react-native';

export const HOME_BLOCKS = ['hero', 'cards', 'tiles', 'assurances', 'guide'] as const;
export type HomeBlock = (typeof HOME_BLOCKS)[number];

/** Where the search is: measuring natural sizes, the tightest, or the guess. */
export type FitPhase = 'natural' | 'tight' | 'check' | 'done';

export interface FitStep {
  phase: FitPhase;
  squeeze: number;
  /** How far the natural layout overflows, remembered for the guess. */
  overflow?: number;
}

/**
 * The next step, given how far the layout at `step.squeeze` overflows the
 * space (negative: room to spare). Pure, so the search is tested on its own.
 */
export function nextFit(step: FitStep, deficit: number): FitStep {
  switch (step.phase) {
    case 'natural':
      return deficit <= 0
        ? { phase: 'done', squeeze: 0 }
        : { phase: 'tight', squeeze: 1, overflow: deficit };
    case 'tight': {
      // Even the tightest overflows. By a few points — a language set taller,
      // a phone a little shorter — it is still the closest to fitting: keep
      // it and scroll that little. By more, the phone is simply too short:
      // its natural sizes, and the scroll it always had.
      if (deficit > 0) return { phase: 'done', squeeze: deficit <= NEAR_FIT ? 1 : 0 };
      const overflow = step.overflow ?? 0;
      // Height falls about linearly with squeeze; a little extra covers the
      // rounding of each size.
      const guess = overflow / (overflow - deficit) + 0.03;
      // The tightest was just measured and fits: nothing left to check.
      if (guess >= 1) return { phase: 'done', squeeze: 1 };
      return { phase: 'check', squeeze: guess, overflow };
    }
    case 'check':
      return { phase: 'done', squeeze: deficit > 0 ? 1 : step.squeeze };
    default:
      return step;
  }
}

/** An overflow at the tightest layout small enough to keep it anyway (pt). */
export const NEAR_FIT = 24;

/** Settled squeezes, so Home coming back into view lays out fitted at once. */
const settled = new Map<string, number>();

/** Long enough for a frame's layout to arrive after the squeeze changes. */
const SETTLE_MS = 40;
/** Past this, a block that has not re-measured did not change size. */
const NO_CHANGE_MS = 250;
/** A settled fit is searched again at most this often for one key. */
const MAX_RESTARTS = 2;

const startFor = (key: string): FitStep =>
  settled.has(key) ? { phase: 'done', squeeze: settled.get(key) ?? 0 } : { phase: 'natural', squeeze: 0 };

export function useFitToScreen(
  key: string,
  /** The fixed gaps between the blocks at a given squeeze. */
  gapsAt: (squeeze: number) => number,
  revealAfterMs = 1200,
) {
  const [, rerender] = useReducer((n: number) => n + 1, 0);

  const keyRef = useRef(key);
  const step = useRef<FitStep>(startFor(key));
  const searchStarted = useRef(Date.now());
  const phaseStarted = useRef(Date.now());
  const restarts = useRef(0);
  const space = useRef<number>();
  const settledSpace = useRef<number>();
  const heights = useRef<Partial<Record<HomeBlock, number>>>({});
  const lastMeasured = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const gaps = useRef(gapsAt);
  gaps.current = gapsAt;

  // A new window size, language or set of cards: search again, from what is
  // already known for it. Done here, while rendering, so this render already
  // lays out at the new key's starting squeeze.
  if (keyRef.current !== key) {
    keyRef.current = key;
    step.current = startFor(key);
    restarts.current = 0;
    searchStarted.current = Date.now();
    phaseStarted.current = Date.now();
  }

  const go = useCallback((next: FitStep) => {
    step.current = next;
    phaseStarted.current = Date.now();
    if (next.phase === 'done') {
      settled.set(keyRef.current, next.squeeze);
      settledSpace.current = space.current;
    }
    rerender();
  }, []);

  const evaluate = useCallback(() => {
    const current = step.current;
    if (space.current === undefined) return;
    const measured = HOME_BLOCKS.map((block) => heights.current[block]);
    if (measured.some((height) => height === undefined)) return;
    const need = (measured as number[]).reduce((sum, height) => sum + height, 0) + gaps.current(current.squeeze);

    if (current.phase === 'done') {
      // Settled, but something moved since — the words re-wrapped, or the
      // space changed under it: a fit that now overflows starts again.
      const overflows = need - space.current > 1 && current.squeeze > 0;
      const moved =
        settledSpace.current !== undefined && Math.abs(settledSpace.current - space.current) > 1;
      if ((overflows || moved) && restarts.current < MAX_RESTARTS) {
        restarts.current += 1;
        settled.delete(keyRef.current);
        searchStarted.current = Date.now();
        go({ phase: 'natural', squeeze: 0 });
      }
      return;
    }
    // The heights must be this squeeze's: wait for a block to re-measure
    // after the change, or long enough to know none will.
    const waited = Date.now() - phaseStarted.current;
    if (lastMeasured.current < phaseStarted.current && waited < NO_CHANGE_MS) {
      clearTimeout(timer.current);
      timer.current = setTimeout(evaluate, NO_CHANGE_MS - waited);
      return;
    }
    go(nextFit(current, need - space.current));
  }, [go]);

  const schedule = useCallback(
    (ms: number) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(evaluate, ms);
    },
    [evaluate],
  );

  const { phase, squeeze } = step.current;

  // After each squeeze is laid out, look again, even if no block moved; and
  // show the body regardless once a search has run too long.
  useEffect(() => {
    if (phase === 'done') return undefined;
    schedule(NO_CHANGE_MS);
    const reveal = setTimeout(rerender, Math.max(0, revealAfterMs - (Date.now() - searchStarted.current)));
    return () => clearTimeout(reveal);
  }, [key, phase, squeeze, schedule, revealAfterMs]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const onBodySpace = useCallback(
    (height: number) => {
      space.current = Math.floor(height);
      schedule(SETTLE_MS);
    },
    [schedule],
  );

  // One handler per block for the life of the screen; each reads the refs,
  // never a captured key.
  const handlers = useRef<Partial<Record<HomeBlock, (event: LayoutChangeEvent) => void>>>({});
  const onBlockLayout = useCallback(
    (block: HomeBlock) => {
      handlers.current[block] ??= (event: LayoutChangeEvent) => {
        heights.current[block] = Math.ceil(event.nativeEvent.layout.height);
        lastMeasured.current = Date.now();
        schedule(SETTLE_MS);
      };
      return handlers.current[block];
    },
    [schedule],
  );

  return {
    squeeze,
    /** False while Home is searching for its fit, so the search is not seen. */
    ready: phase === 'done' || Date.now() - searchStarted.current >= revealAfterMs,
    onBodySpace,
    onBlockLayout,
  };
}
